import type { FeatureConfig } from '~/lib/builder-types';
import type { TechSpecResolver } from './types';

export const apiAccessTechSpec: TechSpecResolver = (
  stack,
  featureConfig?: FeatureConfig,
) => {
  const isMongo = stack.database === 'mongodb';
  const isPostgres =
    stack.database === 'postgres' || stack.database === 'mysql';

  const apiFeatures =
    (featureConfig?.options['api-features'] as string[] | undefined) ?? [];
  const hasWebhooks =
    apiFeatures.includes('webhooks') || apiFeatures.length === 0; // on by default
  const hasGraphQL =
    (featureConfig?.options['api-type'] as string | undefined) === 'graphql';
  const hasRateLimit =
    apiFeatures.includes('rate-limiting') || apiFeatures.length === 0;

  return {
    pages: [
      {
        id: 'api-docs',
        path: '/docs/api',
        label: 'API reference',
        description: hasGraphQL
          ? 'GraphQL Playground with schema explorer and live query execution'
          : 'Auto-generated OpenAPI/Swagger UI — try endpoints directly from the browser',
        authRequired: false,
      },
      {
        id: 'api-keys',
        path: '/settings/api',
        label: 'API keys',
        description:
          'Create, name, scope, and revoke developer API keys — plaintext shown only once on creation',
        authRequired: true,
      },
      {
        id: 'api-key-detail',
        path: '/settings/api/:keyId',
        label: 'API key scopes',
        description: 'Edit name and scope permissions for an existing API key',
        authRequired: true,
      },
      ...(hasWebhooks
        ? [
            {
              id: 'webhooks',
              path: '/settings/webhooks',
              label: 'Webhooks',
              description:
                'Register webhook endpoints, choose event subscriptions, test delivery, view delivery history',
              authRequired: true,
            },
          ]
        : []),
    ],
    uxFlows: [
      {
        id: 'create-api-key',
        title: 'Developer creates an API key',
        steps: [
          'User navigates to /settings/api and clicks "Create key"',
          'User enters a name (e.g. "Production server") and selects scopes (read / write / admin)',
          hasRateLimit
            ? 'User optionally sets a custom rate limit (requests per minute)'
            : 'Key will use the default account rate limit',
          'POST /api/keys creates a random 32-byte token, stores bcrypt hash + key prefix (first 8 chars) in DB',
          'UI shows the plaintext key once — user copies it; it cannot be retrieved again',
          'Developer sends requests with Authorization: Bearer <key> header',
          'Server looks up the key prefix, verifies the hash, checks scopes and rate limit on each request',
        ],
      },
      {
        id: 'revoke-api-key',
        title: 'Developer revokes an API key',
        steps: [
          'User visits /settings/api and finds the key to revoke',
          'User clicks "Revoke" — confirmation dialog warns this is irreversible',
          'DELETE /api/keys/:id deletes the row from DB',
          'Any subsequent request using that key immediately gets 401 Unauthorized',
        ],
      },
      ...(hasWebhooks
        ? [
            {
              id: 'register-webhook',
              title: 'Developer registers a webhook',
              steps: [
                'User visits /settings/webhooks and clicks "Add endpoint"',
                'User enters the destination URL and selects event types to subscribe to',
                'POST /api/webhooks creates the webhook, generates a random HMAC signing secret',
                'User copies the signing secret — displayed once for verification setup',
                'POST /api/webhooks/test sends a sample ping payload to verify the endpoint responds with 200',
              ],
            },
            {
              id: 'webhook-delivery',
              title: 'Webhook is delivered to a registered endpoint',
              steps: [
                'An event occurs in the system (e.g. new user signup, payment completed)',
                'Server looks up all webhook registrations subscribed to that event type',
                'For each webhook, a delivery job is enqueued with the serialized payload',
                'Worker POSTs the payload to the registered URL with X-Webhook-Signature: sha256=<hmac> header',
                'If the endpoint returns 2xx within 10 s — delivery recorded as "success"',
                'If it fails or times out — retry with exponential backoff: 5 s, 30 s, 5 min, 30 min, 2 h (max 5 attempts)',
                'Each attempt is logged in webhook_deliveries with status, HTTP response code, and duration',
                'After max retries, webhook is marked as failing; user notified via email',
              ],
            },
            {
              id: 'rate-limit-hit',
              title: 'API key hits rate limit',
              steps: [
                'Client sends a request with a valid API key',
                'Server checks current request count in Redis/memory cache for the key within the rolling window',
                'If count exceeds limit: server returns 429 Too Many Requests with Retry-After header',
                'Response includes X-RateLimit-Limit and X-RateLimit-Remaining headers on every request',
                'Client should implement exponential backoff and respect Retry-After value',
              ],
            },
          ]
        : []),
    ],
    endpoints: [
      {
        id: 'list-keys',
        method: 'GET',
        path: '/api/keys',
        description:
          'List API keys for the current user — returns metadata only, never the key itself',
        response:
          '{ keys: { id, name, prefix, scopes, lastUsedAt, createdAt }[] }',
      },
      {
        id: 'create-key',
        method: 'POST',
        path: '/api/keys',
        description: 'Create a new API key — returns plaintext once',
        requestBody: '{ name, scopes: string[], rateLimitRpm?, expiresAt? }',
        response: '{ key: string (plaintext, shown once), meta: ApiKey }',
      },
      {
        id: 'update-key',
        method: 'PATCH',
        path: '/api/keys/:id',
        description: 'Update API key name or scopes (does not rotate the key)',
        requestBody: '{ name?, scopes? }',
        response: '{ meta: ApiKey }',
      },
      {
        id: 'revoke-key',
        method: 'DELETE',
        path: '/api/keys/:id',
        description: 'Permanently revoke an API key — takes effect immediately',
      },
      ...(hasWebhooks
        ? [
            {
              id: 'list-webhooks',
              method: 'GET' as const,
              path: '/api/webhooks',
              description:
                'List registered webhook endpoints for the current user',
              response: '{ webhooks: Webhook[] }',
            },
            {
              id: 'create-webhook',
              method: 'POST' as const,
              path: '/api/webhooks',
              description: 'Register a new webhook endpoint',
              requestBody: '{ url, events: string[], enabled? }',
              response:
                '{ webhook: Webhook, signingSecret: string (shown once) }',
            },
            {
              id: 'update-webhook',
              method: 'PATCH' as const,
              path: '/api/webhooks/:id',
              description: 'Update webhook URL, events, or enabled state',
              requestBody: '{ url?, events?, enabled? }',
              response: '{ webhook: Webhook }',
            },
            {
              id: 'delete-webhook',
              method: 'DELETE' as const,
              path: '/api/webhooks/:id',
              description: 'Remove a webhook registration',
            },
            {
              id: 'test-webhook',
              method: 'POST' as const,
              path: '/api/webhooks/:id/test',
              description:
                'Send a test ping payload to verify the endpoint is reachable',
              response: '{ status: number, durationMs: number }',
            },
            {
              id: 'webhook-deliveries',
              method: 'GET' as const,
              path: '/api/webhooks/:id/deliveries',
              description:
                'List delivery attempts for a webhook — paginated, newest first',
              response:
                '{ deliveries: { id, eventType, status, responseCode, durationMs, createdAt }[] }',
            },
          ]
        : []),
    ],
    dbTables: isMongo
      ? [
          {
            id: 'api_keys',
            name: 'api_keys',
            columns: [
              { name: '_id', type: 'ObjectId', nullable: false },
              { name: 'userId', type: 'ObjectId ref users', nullable: false },
              { name: 'name', type: 'string', nullable: false },
              {
                name: 'keyHash',
                type: 'string',
                nullable: false,
                notes: 'bcrypt hash of the full key — never store plaintext',
              },
              {
                name: 'prefix',
                type: 'string',
                nullable: false,
                notes: 'First 8 chars of key for display and fast lookup',
              },
              { name: 'scopes', type: 'string[]', nullable: false },
              {
                name: 'rateLimitRpm',
                type: 'number',
                nullable: true,
                notes: 'Requests per minute — null means use account default',
              },
              { name: 'lastUsedAt', type: 'Date', nullable: true },
              { name: 'expiresAt', type: 'Date', nullable: true },
              { name: 'createdAt', type: 'Date', nullable: false },
            ],
            notes: 'Index { prefix } for fast key lookup on each API request.',
          },
          ...(hasWebhooks
            ? [
                {
                  id: 'webhooks',
                  name: 'webhooks',
                  columns: [
                    { name: '_id', type: 'ObjectId', nullable: false },
                    {
                      name: 'userId',
                      type: 'ObjectId ref users',
                      nullable: false,
                    },
                    { name: 'url', type: 'string', nullable: false },
                    { name: 'events', type: 'string[]', nullable: false },
                    {
                      name: 'secretHash',
                      type: 'string',
                      nullable: false,
                      notes:
                        'HMAC secret — hashed for storage, used to sign payloads',
                    },
                    { name: 'enabled', type: 'boolean', nullable: false },
                    {
                      name: 'failureCount',
                      type: 'number',
                      nullable: false,
                      notes:
                        'Consecutive failure count — auto-disable after threshold',
                    },
                    { name: 'createdAt', type: 'Date', nullable: false },
                  ],
                },
                {
                  id: 'webhook_deliveries',
                  name: 'webhook_deliveries',
                  columns: [
                    { name: '_id', type: 'ObjectId', nullable: false },
                    {
                      name: 'webhookId',
                      type: 'ObjectId ref webhooks',
                      nullable: false,
                    },
                    { name: 'eventType', type: 'string', nullable: false },
                    {
                      name: 'status',
                      type: 'string',
                      nullable: false,
                      notes: 'success | failed | pending',
                    },
                    { name: 'responseCode', type: 'number', nullable: true },
                    { name: 'durationMs', type: 'number', nullable: true },
                    { name: 'attempt', type: 'number', nullable: false },
                    { name: 'createdAt', type: 'Date', nullable: false },
                  ],
                  notes:
                    'TTL index on createdAt to expire records after 30 days.',
                },
              ]
            : []),
        ]
      : [
          {
            id: 'api_keys',
            name: 'api_keys',
            columns: [
              { name: 'id', type: 'uuid', nullable: false },
              { name: 'user_id', type: 'uuid FK users', nullable: false },
              { name: 'name', type: 'text', nullable: false },
              {
                name: 'key_hash',
                type: 'text',
                nullable: false,
                notes: 'bcrypt hash — never log or store the plaintext key',
              },
              {
                name: 'prefix',
                type: 'text',
                nullable: false,
                notes:
                  'First 8 chars of key for display; indexed for O(1) lookup per request',
              },
              {
                name: 'scopes',
                type: isPostgres ? 'text[]' : 'text (JSON)',
                nullable: false,
              },
              {
                name: 'rate_limit_rpm',
                type: 'integer',
                nullable: true,
                notes: 'Per-key override; null = use account-level default',
              },
              { name: 'last_used_at', type: 'timestamp', nullable: true },
              {
                name: 'expires_at',
                type: 'timestamp',
                nullable: true,
                notes: 'Null = never expires',
              },
              { name: 'created_at', type: 'timestamp', nullable: false },
            ],
            notes:
              'Index prefix for fast lookup. Index (user_id) for listing. Reject requests where expires_at < now().',
          },
          ...(hasWebhooks
            ? [
                {
                  id: 'webhooks',
                  name: 'webhooks',
                  columns: [
                    { name: 'id', type: 'uuid', nullable: false },
                    { name: 'user_id', type: 'uuid FK users', nullable: false },
                    { name: 'url', type: 'text', nullable: false },
                    {
                      name: 'events',
                      type: isPostgres ? 'text[]' : 'text (JSON)',
                      nullable: false,
                      notes:
                        'List of subscribed event types e.g. ["user.created", "payment.completed"]',
                    },
                    {
                      name: 'secret_hash',
                      type: 'text',
                      nullable: false,
                      notes:
                        'Hashed HMAC secret used to sign outgoing payloads',
                    },
                    { name: 'enabled', type: 'boolean', nullable: false },
                    {
                      name: 'failure_count',
                      type: 'integer',
                      nullable: false,
                      notes:
                        'Auto-disable webhook after 10 consecutive failures',
                    },
                    { name: 'created_at', type: 'timestamp', nullable: false },
                  ],
                },
                {
                  id: 'webhook_deliveries',
                  name: 'webhook_deliveries',
                  columns: [
                    { name: 'id', type: 'uuid', nullable: false },
                    {
                      name: 'webhook_id',
                      type: 'uuid FK webhooks',
                      nullable: false,
                    },
                    { name: 'event_type', type: 'text', nullable: false },
                    {
                      name: 'status',
                      type: 'text',
                      nullable: false,
                      notes: 'success | failed | pending',
                    },
                    { name: 'response_code', type: 'integer', nullable: true },
                    { name: 'duration_ms', type: 'integer', nullable: true },
                    {
                      name: 'attempt',
                      type: 'integer',
                      nullable: false,
                      notes: '1-based attempt count; max 5 before giving up',
                    },
                    { name: 'created_at', type: 'timestamp', nullable: false },
                  ],
                  notes:
                    'Index (webhook_id, created_at DESC) for delivery history queries. Archive or delete records older than 30 days.',
                },
              ]
            : []),
        ],
  };
};
